import type { ComputedRef, Ref } from 'vue';
import type { LocationQuery } from 'vue-router';
import type {
  InitialData as WeeklyReportSearchFormInitialData,
  ValidData as WeeklyReportSearchFormValidData,
} from '@/components/searchForms/WeeklyReportSearchForm.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { format } from 'date-fns';
import _isEqual from 'lodash/isEqual';

import { useDialogStore } from '@/stores/useDialogStore';
import { useUserStore } from '@/stores/useUserStore';
import {
  fetchGetRunSheetReport,
  fetchGetRunSheetReportFile,
  fetchPostReportReturnLabelsFile,
} from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';
import {
  NumberStringTuple,
  RunSheetDriversByDay,
  RunSheetReportDayData,
  RunSheetTableData,
} from '@/models/reports';

export type ReturnLabelsFormat = openapi.PostReportReturnLabels_Body['format'];

export interface RunSheetReportViewComposable {
  search: (formData: WeeklyReportSearchFormValidData) => void;
  initialData: ComputedRef<WeeklyReportSearchFormInitialData>;
  isFetchingData: Ref<boolean>;
  isFetchingFileDownload: Ref<boolean>;
  isFetchingReturnLabels: Ref<boolean>;
  isActionDisabled: ComputedRef<boolean>;
  tableData: Ref<RunSheetTableData | null>;
  handleDriverSelected: (day: string, driver: string) => void;
  handleDaySelected: (day: string) => void;
  handleDownloadRunSheetReport: () => void;
  handleDownloadReturnLabels: (format: ReturnLabelsFormat) => void;
  handlePrintReturnLabels: () => void;
}

export default function useRunSheetReportView(
  queryDates: Readonly<Ref<[Date, Date] | undefined>>,
  queryStates: Readonly<Ref<string[] | undefined>>,
): RunSheetReportViewComposable {
  const router = useRouter();
  const route = useRoute();
  const userStore = useUserStore();
  const dialogStore = useDialogStore();

  // downloads keep the backend default; the print dialog lets the user pick
  const defaultReturnLabelsSize: openapi.PostReportReturnLabels_Body['size'] =
    '4x4';

  const userTimeZone = computed(() => userStore.user.timezone);

  const initialData = computed<WeeklyReportSearchFormInitialData>(() => {
    let states: string[] | undefined;
    if (queryStates.value) states = queryStates.value.slice();
    return {
      dates: queryDates.value,
      states,
    };
  });

  const isFetchingData = ref(false);
  const isFetchingFileDownload = ref(false);
  const isFetchingReturnLabels = ref(false);
  const tableData = ref<RunSheetTableData | null>(null);

  function search(formData: WeeklyReportSearchFormValidData): void {
    const dates = [
      format(formData.dates[0], 'yyyy-MM-dd'),
      format(formData.dates[1], 'yyyy-MM-dd'),
    ].join(',');
    const query = { ...route.query, dates, states: formData.states.join(',') };
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: routeNames.RUN_SHEET,
      query,
      force: true,
      replace: isReplace,
    });
  }

  // the [day, driver] pairs every report/labels endpoint takes as its payload
  const chosenDriversByDay = computed<[string, number][]>(() => {
    if (!tableData.value) return [];
    return Object.entries(tableData.value.driverCheckboxes).flatMap(
      ([day, driversOfDay]) =>
        Object.entries(driversOfDay)
          .filter(([, isChosen]) => isChosen)
          .map(([driver]): [string, number] => [day, Number(driver)]),
    );
  });

  // every footer action needs at least one chosen driver and an idle page
  const isActionDisabled = computed(
    () =>
      isFetchingData.value ||
      isFetchingFileDownload.value ||
      isFetchingReturnLabels.value ||
      !chosenDriversByDay.value.length,
  );

  function handleDownloadRunSheetReport(): void {
    if (!chosenDriversByDay.value.length) return;

    isFetchingFileDownload.value = true;
    void fetchGetRunSheetReportFile(chosenDriversByDay.value).finally(() => {
      isFetchingFileDownload.value = false;
    });
  }

  function handleDownloadReturnLabels(format: ReturnLabelsFormat): void {
    if (!chosenDriversByDay.value.length) return;

    isFetchingReturnLabels.value = true;
    void fetchPostReportReturnLabelsFile({
      drivers: chosenDriversByDay.value,
      size: defaultReturnLabelsSize,
      format,
    }).finally(() => {
      isFetchingReturnLabels.value = false;
    });
  }

  function handlePrintReturnLabels(): void {
    if (!chosenDriversByDay.value.length) return;

    dialogStore.open('returnLabelsPrint', {
      drivers: chosenDriversByDay.value,
    });
  }

  async function fetchData(newQuery: LocationQuery): Promise<void> {
    const dates = queryDates.value;
    const states = queryStates.value;

    if (!dates || !states) return;
    isFetchingData.value = true;

    await fetchGetRunSheetReport(userTimeZone.value, { dates, states })
      .then((data: RunSheetReportDayData) => {
        if (!_isEqual(route.query, newQuery)) {
          return;
        }

        const daysCheckboxes = Object.keys(data).reduce(
          (accumulator, value) => {
            return { ...accumulator, [value]: false };
          },
          {},
        );

        let newDrArr = {};
        Object.keys(data).forEach((item: string) => {
          const driverKeys = data[item].reduce(
            (val: RunSheetDriversByDay, driv: NumberStringTuple) => {
              return { ...val, [driv[0]]: false };
            },
            {},
          );
          newDrArr = { ...newDrArr, ...{ [item]: driverKeys } };
        });

        tableData.value = {
          reports: data,
          daysCheckboxes,
          driverCheckboxes: newDrArr,
        };
      })
      .finally(() => {
        isFetchingData.value = false;
      });
  }

  function handleDriverSelected(day: string, driver: string): void {
    if (tableData.value) {
      const allDriversChosen = Object.values(
        // TODO
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        tableData.value?.driverCheckboxes[day],
        // TODO
        /* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */
      ).every((v) => !!v);
      tableData.value.daysCheckboxes[day] = allDriversChosen;
    }
  }

  function handleDaySelected(day: string): void {
    if (tableData.value) {
      const newValue = tableData.value.daysCheckboxes[day];
      Object.values(tableData.value.driverCheckboxes[day]).map(
        (driver: boolean, idx: number) => {
          if (tableData.value) {
            const driverNumber = Number(
              Object.keys(tableData.value.driverCheckboxes[day])[idx],
            );
            tableData.value.driverCheckboxes[day][driverNumber] = newValue;
          }
          return null;
        },
      );
    }
  }

  watch(
    route,
    async (newRoute) => {
      if (newRoute.name === routeNames.RUN_SHEET) {
        await fetchData(newRoute.query);
      }
    },
    { immediate: true },
  );

  return {
    search,
    initialData,
    isFetchingData,
    isFetchingFileDownload,
    isFetchingReturnLabels,
    isActionDisabled,
    tableData,
    handleDriverSelected,
    handleDaySelected,
    handleDownloadRunSheetReport,
    handleDownloadReturnLabels,
    handlePrintReturnLabels,
  };
}
